var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { memo, useMemo, useState } from 'react';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LockIcon from '@mui/icons-material/Lock';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import { isDropComingSoon } from 'root/src/shared/drops/helpers/isDropComingSoon.helper';
import { getClassifiedDarePlatform } from 'root/src/shared/drops/helpers/dropDare.helpers';
import { RewardType, KeyType } from 'root/src/shared/rewards/constants';
import { KeyProductMnemonicIdResolver } from 'root/src/shared/rewards/services/productResolver.service';
import { DarePlatform } from 'root/src/shared/@types/platforms';
import { ProjectStatus } from 'root/src/shared/@types/project';
import { useAcceptDare } from 'root/src/client/v2/drop/hooks/useAcceptDare';
import { useDareAssignee } from 'root/src/client/logic/project/hooks/useDareAssignee';
import { RedesignKeySelectionModal } from 'root/src/client/v2/drop/features/KeySelectionModal/components/RedesignKeySelectionModal';
import { RedesignInitialProductDisplayModal } from 'root/src/client/v2/drop/features/KeySelectionModal/components/RedesignInitialProductDisplayModal';
import { useIsAuthenticated } from 'root/src/client/v2/auth/hooks/useIsAuthenticated';
import { useMaxDaresByPlatforms } from 'root/src/client/logic/dropEvent/hooks/useMaxDaresByPlatforms';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
import { RedesignDeliverDareModal } from 'root/src/client/v2/_pages/drop/redesign/RedesignDeliverDareModal';
import { useRedesignSubmissionPlatformModal } from 'root/src/client/v2/_pages/drop/redesign/RedesignSubmissionPlatformModal';
import { useRedesignPreferredPlatformReachedMaxDaresModal } from 'root/src/client/v2/_pages/drop/redesign/RedesignPreferredPlatformReachedMaxDaresModal';
import { useRedesignGameIntegratedPermissionModal } from 'root/src/client/v2/_pages/drop/redesign/RedesignGameIntegratedPermissionModal';
import { useRedesignReauthModal } from 'root/src/client/v2/_pages/drop/redesign/RedesignReauthModal';
import { RedesignHowToEarnPointsModal } from 'root/src/client/v2/_pages/drop/redesign/RedesignHowToEarnPointsModal';
import { RedesignNoMoreRewardModal } from 'root/src/client/v2/_pages/drop/redesign/RedesignNoMoreRewardModal';
import { RedesignDeliveryRatioTooLowModal } from 'root/src/client/v2/_pages/drop/redesign/RedesignDeliveryRatioTooLowModal';
import { RedesignVerificationPendingModal } from 'root/src/client/v2/_pages/drop/redesign/RedesignVerificationPendingModal';
import { RedesignPlatformRequirementModal, formatPlatformLabels } from 'root/src/client/v2/_pages/drop/redesign/RedesignPlatformRequirementModal';
import { SignInDialog } from 'root/src/client/v2/auth/features/SignInDialog/SignInDialog';
import { DropNotifyButton } from 'root/src/client/v2/_pages/drop/redesign/DropNotifyButton';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from 'root/src/client/v2/common/components/ui/tooltip';
import { cn } from 'root/src/client/v2/common/helpers/cn';
// Shared between RedesignDareCard (grid view) and RedesignDareRow (list view) so the two presentations
// of the same dare cannot drift apart on eligibility rules or accept/deliver behaviour.
const PRODUCT_TYPE_LABEL = {
    dropKey: 'Game Key',
    dropCode: 'Code',
    customProduct: 'Custom Reward',
    bundle: 'Bundle',
};
// Reward-name fallback: maps product.type to a label when product.name is empty.
export const dareRewardName = (dare) => {
    var _a, _b, _c;
    return (((_a = dare.product) === null || _a === void 0 ? void 0 : _a.name) || PRODUCT_TYPE_LABEL[(_c = (_b = dare.product) === null || _b === void 0 ? void 0 : _b.type) !== null && _c !== void 0 ? _c : ''] || 'Reward');
};
// DLC detection from the key mnemonicId (`dropGame-X|platform|region|dlc-<hash>`): the dare payload
// carries no keyType field, and adding one server-side would trip the legacy drop response schema
// (additionalProperties: false), so resolve it client-side from the id the payload already has.
export const isDlcDareReward = (dare) => {
    var _a;
    if (((_a = dare.product) === null || _a === void 0 ? void 0 : _a.type) !== RewardType.KEY || !dare.product.id) {
        return false;
    }
    try {
        return new KeyProductMnemonicIdResolver(dare.product.id).type === KeyType.DLC;
    }
    catch (_b) {
        return false;
    }
};
// One style for every level (the former n00b cyan) rather than a colour per level: the level is already
// spelled out in the badge text, and colour-coding it competed with the status pills for attention.
export const DARE_DIFFICULTY_CLASS = 'border-neon-cyan/50 text-neon-cyan shadow-[0_0_12px_-3px_hsl(var(--neon-cyan)/0.6)]';
// Teal DLC marker shared by the card and row reward blocks, so DLC keys read differently from
// full-game keys even when the truncated name hides the "DLC" part. Teal (not the earlier purple
// tint, which sank into the dark card) with stronger border/background alphas for visibility.
export const DlcChip = () => (<span className="shrink-0 rounded border border-solid border-neon-teal/50 bg-neon-teal/15 px-1.5 py-px text-[10px] font-bold uppercase tracking-wider text-neon-teal">
		DLC
	</span>);
// Gated on the platform as well as the flag: the rule is enforced in the Twitch delivery strategy
// alone (server/modules/dares/services/validator/strategies/twitch.strategy.ts), and dare records
// written before the authoring form started clearing it can still carry the flag on a non-Twitch dare.
export const isClipsOnlyDare = (dare) => {
    var _a;
    return (Boolean(dare.clipsOnly) && Boolean((_a = dare.platforms) === null || _a === void 0 ? void 0 : _a.includes(DarePlatform.Twitch)));
};
// Carries the legacy dare card's clips-only warning (web/list/listItemComponents/dare/DareCardSeparator.js)
// into the redesign. The legacy version was a bare error icon with a hover tooltip and no label, which
// is why it was missed in the port; without it the first a creator hears of the rule is the rejection
// toast after they have already delivered a VOD.
// `onOpenChange` lets a host card stand its own hover panel down while this tooltip is up; it keeps
// its own provider so the chip still works on its own outside such a card.
export const ClipsOnlyChip = ({ onOpenChange }) => (<TooltipProvider delayDuration={150}>
		<Tooltip onOpenChange={onOpenChange}>
			<TooltipTrigger asChild>
				<span className="inline-flex shrink-0 cursor-help items-center gap-1 rounded border border-solid border-neon-amber/50 bg-neon-amber/15 px-1.5 py-px text-[10px] font-bold uppercase tracking-wider text-neon-amber">
					<ContentCutIcon sx={{ fontSize: 11 }}/>
					Clips only
				</span>
			</TooltipTrigger>
			<TooltipContent className="max-w-[240px]">
				Twitch deliveries for this dare have to be a clip. A VOD link will be rejected.
			</TooltipContent>
		</Tooltip>
	</TooltipProvider>);
const StatePill = ({ className, icon, label }) => (<div className={cn('flex w-full items-center justify-center gap-1.5 rounded-lg border border-solid py-2 text-xs font-semibold', className)}>
		{icon}
		{label}
	</div>);
// The full state machine behind a dare's call to action: which control to show (accept / deliver /
// a blocked-state pill) plus the handlers driving it, wired to the reused accept flow and the
// redesign modal stack.
export const useDareCta = ({ drop, dare, refetchDropEvent }) => {
    var _a;
    const redesignModal = useRedesignModal();
    const displaySubmissionPlatformModal = useRedesignSubmissionPlatformModal();
    const displayPreferredPlatformReachedMaxDaresModal = useRedesignPreferredPlatformReachedMaxDaresModal();
    const displayGameIntegratedPermissionModal = useRedesignGameIntegratedPermissionModal();
    const displayReauthModal = useRedesignReauthModal();
    const { onAcceptDare } = useAcceptDare({
        drop,
        dare,
        refetchDropEvent,
        keySelectionModalComponent: RedesignKeySelectionModal,
        initialProductDisplayModalComponent: RedesignInitialProductDisplayModal,
        showModal: redesignModal.show,
        displaySubmissionPlatformModal,
        displayPreferredPlatformReachedMaxDaresModal,
        displayGameIntegratedPermissionModal,
        displayReauthModal,
        displayHowToEarnPointsModal: () => redesignModal.show(<RedesignHowToEarnPointsModal drop={drop}/>),
        displayNoMoreRewardModal: formData => redesignModal.show(<RedesignNoMoreRewardModal formData={formData} onSuccess={refetchDropEvent}/>),
        displayDeliveryRatioTooLowModal: campaigns => redesignModal.show(<RedesignDeliveryRatioTooLowModal campaigns={campaigns}/>),
        displayVerificationPendingModal: () => redesignModal.show(<RedesignVerificationPendingModal />, { closeable: false }),
    });
    const onDeliver = () => {
        var _a;
        return redesignModal.show(<RedesignDeliverDareModal projectUrl={(_a = dare.projectUrl) !== null && _a !== void 0 ? _a : ''} platforms={dare.platforms} clipsOnly={isClipsOnlyDare(dare)} onSuccess={refetchDropEvent}/>);
    };
    const isAuthenticated = useIsAuthenticated();
    // Two different scopes, deliberately. `dropMaxDares` (best of every connected platform) is what the
    // section's "dares left" counter shows, so the maxed-out state is keyed off it. `dareMaxDares` is
    // scoped to THIS dare's platforms, which is what the server validates the accept against
    // (validateAssignees runs calculateMaxDaresByPlatform on the assignee's platform alone). Gating on
    // the drop-wide figure let a Twitch-qualified creator click Accept on a TikTok-only dare and only
    // learn they were short on TikTok from the rejection toast.
    const dropMaxDares = useMaxDaresByPlatforms({ fallbackDrop: drop });
    const dareMaxDares = useMaxDaresByPlatforms({ platforms: dare.platforms, fallbackDrop: drop });
    // Same predicate the accept flow gates on, so the CTA cannot offer an accept the flow would drop.
    const assignee = useDareAssignee({ dare, fallbackDrop: drop });
    // The accounts that would unlock this dare, classified to what the creator actually connects
    // (a YouTube Shorts dare is satisfied by a YouTube account), matching how useDareAssignee matches.
    const requiredPlatforms = useMemo(() => {
        const classified = dare.platforms
            .map(getClassifiedDarePlatform)
            .filter((platform) => Boolean(platform));
        return Array.from(new Set(classified));
    }, [dare.platforms]);
    const [accepting, setAccepting] = useState(false);
    // Own the loading state for the WHOLE accept flow (pre-checks, userHasGame refetch, then modal-open
    // or mutation). The hook only flips its own hover on the mutation path, so delegating to it left
    // the button idle while the key selection modal loaded.
    const onAccept = () => __awaiter(void 0, void 0, void 0, function* () {
        setAccepting(true);
        try {
            yield onAcceptDare();
        }
        finally {
            setAccepting(false);
        }
    });
    const onShowPlatformRequirement = () => redesignModal.show(<RedesignPlatformRequirementModal platforms={requiredPlatforms}/>);
    const { status } = dare;
    const isCompleted = status === ProjectStatus.ProjectDelivered;
    const isAccepted = status === ProjectStatus.ProjectAccepted || status === ProjectStatus.Pending;
    const isInReview = status === ProjectStatus.DeliveryPending;
    const isExpired = dare.expirationDate ? new Date(dare.expirationDate).getTime() < Date.now() : false;
    const isSoldOut = dare.capacity ? dare.capacity.available <= 0 : false;
    const acceptedDares = (_a = drop.creatorAcceptedDares) !== null && _a !== void 0 ? _a : 0;
    const comingSoon = isDropComingSoon(drop);
    let cta = 'action';
    if (isCompleted) {
        cta = 'completed';
    }
    else if (isAccepted) {
        cta = 'accepted';
    }
    else if (isInReview) {
        cta = 'inReview';
    }
    else if (comingSoon) {
        // Ranked below the per-dare progress states so a creator who already has this dare in flight keeps
        // their Deliver/In review CTA, but above every "cannot accept" state: the drop is not live yet, so
        // accepting is impossible regardless of eligibility or capacity.
        cta = 'comingSoon';
    }
    else if (isExpired) {
        cta = 'expired';
    }
    else if (isSoldOut) {
        cta = 'soldOut';
    }
    else if (!isAuthenticated) {
        cta = 'signin';
    }
    else if (!assignee && requiredPlatforms.length > 0) {
        // No connected account on any platform this dare accepts. Ranked above `ineligible` because
        // "you have no TikTok account" is the more specific and actionable reason than "not enough views".
        // Requires a named platform to point the creator at, otherwise fall through to the generic states.
        cta = 'notConnected';
    }
    else if (dareMaxDares <= 0) {
        cta = 'ineligible';
    }
    else if (acceptedDares >= dropMaxDares) {
        cta = 'maxed';
    }
    return { cta, accepting, onAccept, onDeliver, requiredPlatforms, onShowPlatformRequirement };
};
// Renders the right control for the dare's CTA state, always full width with no outer margin: the
// consumer owns the spacing (the card pads its footer, the row sizes its action column).
export const DareCta = memo(({ drop, dare, refetchDropEvent }) => {
    const { cta, accepting, onAccept, onDeliver, requiredPlatforms, onShowPlatformRequirement } = useDareCta({ drop, dare, refetchDropEvent });
    return (<>
			{cta === 'completed' && (<StatePill className="border-neon-green/40 bg-neon-green/15 text-neon-green" icon={<EmojiEventsIcon sx={{ fontSize: 14 }}/>} label="Completed"/>)}
			{/*
            Green, not the brand gradient, on purpose: an accepted dare used to render the same purple
            button as an unaccepted one, so scanning the list gave no clue which dares a creator had
            already taken on. Solid green (act on it now) also stays a step louder than the tinted green
            "Completed" pill (nothing left to do), keeping the two apart at a glance.
        */}
			{cta === 'accepted' && (<Button variant="success" onClick={onDeliver} className="w-full rounded-lg text-xs font-semibold">
					Deliver dare
				</Button>)}
			{cta === 'inReview' && (<StatePill className="border-neon-amber/30 bg-neon-amber/10 text-neon-amber" icon={<ScheduleIcon sx={{ fontSize: 14 }}/>} label="In review"/>)}
			{cta === 'comingSoon' && (<DropNotifyButton dropId={drop.id} label="Notify me" className="w-full rounded-lg text-xs font-semibold"/>)}
			{cta === 'expired' && (<StatePill className="border-white/[0.06] bg-secondary text-muted-foreground" icon={<LockIcon sx={{ fontSize: 14 }}/>} label="Expired"/>)}
			{cta === 'soldOut' && (<StatePill className="border-white/[0.06] bg-secondary text-muted-foreground" icon={<LockIcon sx={{ fontSize: 14 }}/>} label="No keys left"/>)}
			{cta === 'notConnected' && (<Button variant="secondary" onClick={onShowPlatformRequirement} className="w-full gap-1.5 rounded-lg text-xs font-semibold">
					<LockIcon sx={{ fontSize: 14 }}/>
					Connect {formatPlatformLabels(requiredPlatforms)}
				</Button>)}
			{cta === 'ineligible' && (<TooltipProvider delayDuration={150}>
					<Tooltip>
						<TooltipTrigger asChild>
							<div className="w-full cursor-help">
								<StatePill className="border-white/[0.06] bg-secondary text-muted-foreground" icon={<LockIcon sx={{ fontSize: 14 }}/>} label="Need more viewers"/>
							</div>
						</TooltipTrigger>
						<TooltipContent className="max-w-[240px]">
							Your {formatPlatformLabels(requiredPlatforms)} viewership is below this drop&apos;s requirement,
							so you cannot accept this dare yet.
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>)}
			{cta === 'maxed' && (<StatePill className="border-white/[0.06] bg-secondary text-muted-foreground" icon={<LockIcon sx={{ fontSize: 14 }}/>} label="Max dares reached"/>)}
			{cta === 'signin' && (<SignInDialog>
					<Button variant="brand" className="w-full rounded-lg text-xs font-semibold">Sign in to accept</Button>
				</SignInDialog>)}
			{cta === 'action' && (<Button variant="brand" loading={accepting} onClick={onAccept} className="w-full rounded-lg text-xs font-semibold">
					Accept Dare
				</Button>)}
		</>);
});
